import { cache } from '../../cache-impl/cache-registry.js';
import { createNewQuote } from '../../quotes/quote-creator.js';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { fireQuickErrorToast, fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away.js';
import {
  FranchiseeQuoteContainerManager,
  mapPaymentProfileCalculation
} from '../../quotes/data/franchisee-quote-manager';
import { getApiFactory } from '../../../api/api-injector';
import { getQuoteContainerManager } from '../../quotes/quote-ui-adapter.js';
import { getSupplier } from '../../quotes/supplier-selector.js';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { LangFranchiseeProjectManager } from '../../../../localization/scripts/dealer-franchisee/projects/data/franchisee-project-manager-lang';
import { launchQuote } from '../../../quotes/ui/launcher.js';
import { ProjectContainerManager } from '../../../projects/data/project-container';
import { QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { ResourceType } from '../../../api/dealer-api-interface-project.js';
import { userDataStore } from '../../common/current-user-data-store.js';

export class FranchiseeProjectContainerManager extends ProjectContainerManager {
  private projectCache = cache().project;
  private clientCache = cache().client;
  private clientTypeCache = cache().paymentProfile;
  private contactCache = cache().contact;
  private termsCache = cache().termsAndConditions;

  private termsConditionsApi = getApiFactory().termsAndConditions();

  override async createProjectQuote(): Promise<boolean> {
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return false;

    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return false;
    }
    const quoteContainer = await createNewQuote({
      supplierId: supplier.supplierId,
      owner: userDataStore.defaultBranch.id
    });
    if (quoteContainer) {
      const quoteManager = getQuoteContainerManager(quoteContainer) as FranchiseeQuoteContainerManager;
      quoteManager.quote.state = QuoteState.Active;
      quoteManager.quote.budget = this.project.budget;
      quoteManager.quote.title = `${this.project.title} - ${quoteManager.quote.title}`;
      quoteManager.quote.description = this.project.description ?? '';
      quoteManager.generateQuoteNumberOnSave();

      const branchQuote = quoteManager.branchQuote;
      const client = this.clientCache.getLocal(this.project.clientId);

      branchQuote.clientId = this.project.clientId;
      branchQuote.clientName = client?.displayValue ?? '';
      branchQuote.clientTypeId = this.project.clientTypeId;
      branchQuote.clientTypeName = this.clientTypeCache.getLocal(branchQuote.clientTypeId)?.displayValue ?? '';
      branchQuote.contactId = this.project.contactId;
      branchQuote.contactName = this.contactCache.getLocal(branchQuote.contactId)?.displayValue ?? '';

      const paymentProfile = await this.clientTypeCache.get(branchQuote.clientTypeId);
      if (paymentProfile?.data) {
        const profile = paymentProfile.data;
        quoteManager.quotePrice.percentMarginOrMarkup = profile.paymentProfile.value;
        quoteManager.quotePrice.calculation = mapPaymentProfileCalculation(profile.paymentProfile.calculation);
      }

      // terms and conditions
      let termsId = this.project.termsAndConditionsId ?? client?.data.franchiseeClient?.termsAndConditionsId;

      // Check if there is a default terms and conditions for the branch if not found on project or client
      if (!termsId || termsId === emptyGuid) {
        termsId = (
          await this.termsConditionsApi.getDefaultTermsAndConditions({ branchId: userDataStore.defaultBranch.id })
        )?.termsAndConditions?.id;
      }

      if (termsId && termsId !== emptyGuid) {
        const termsConditions = await this.termsCache.get(termsId);
        if (termsConditions?.data) {
          quoteManager.quote.termsAndConditions = termsConditions.data.termsAndConditions.terms;
        }
      }

      const updatedQuote = await quoteManager.saveQuote(true);
      if (updatedQuote) {
        const resource = await this.api.createProjectResourceReference({
          resourceId: quoteContainer.quoteId,
          projectId: this.projectId,
          typeOf: ResourceType.Quote,
          parentResourceId: emptyGuid,
          parentTypeOf: ResourceType.None
        });
        if (resource) {
          await Promise.all([
            this.projectCache.updateLocal(this.projectId),
            cache().projectResourceLink.updateLocal(this.projectId),
            cache().projectResourceLink.updateLocal(quoteContainer.quoteId)
          ]);
          fireQuickSuccessToast(
            LangFranchiseeProjectManager.createdQuoteForProject({ projectTitle: this.project.title })
          );
          return launchQuote(quoteContainer.quoteId);
        }
      }
      // if we get here A) The quote update has failed and B) the project-quote link wasn't created
      fireQuickErrorToast(
        LangFranchiseeProjectManager.unableToCreateQuoteForProject({ projectTitle: this.project.title })
      );
      // if we were unable to create the project link, remove the alternative quote
      return await quoteManager.deleteQuote();
    }
    fireQuickErrorToast(LangFranchiseeProjectManager.unableToCreateQuote());
    return false;
  }
}
